/* eslint-disable */
import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Box, Typography, Button, Divider, Stack, CircularProgress, IconButton, InputAdornment, styled, useTheme } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import CustomTextField from "../forms/theme-elements/CustomTextField";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import { checkEmail, preSignUp } from "../../gql/mutations/auth";
import { validateAlphabetsString, validateEmail, validatePassword } from "../Validate/validate";
import MessagePopUp from "../Modal/messagePopUp";
import useQuery from "../../hooks/allQuery";
import { usePopup } from "../../context/commonPop";
import LoginGoogle from "../../googleAuth/loginGoogle";
import { popupType } from "../../common/constant";
import { debounce } from "lodash";
import { isValidEmail } from "../../utils/validations";
import { encryptPassword } from "../../utils/myUtills";

const PassWordShowIconWrapper = styled(InputAdornment)(({ theme }) => ({
  "& button": {
    "&:focus": {
      outline: "none",
    },
    "& svg": {
      color: "#0000008a",
    },
  },
}));

function AuthRegister({ title, subtitle, subtext }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkEmailQuery] = useLazyQuery(checkEmail, { fetchPolicy: "network-only" });

  const [showPasswordState, setShowPasswordState] = useState({
    password: false,
    confirmPassword: false,
  });
  const [signup, setSignup] = useState({
    name: "",
    email: "",
    password: "",
    password2: "",
  });
  const [errorState, setErrorState] = useState({
    name: false,
    email: false,
    password: false,
    password2: false,
  });
  const togglePasswordVisibility = field => {
    setShowPasswordState(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const [PRESIGNUP] = useMutation(preSignUp);
  const { allQuery } = useQuery();
  const { handleOpen, customPopupModel } = usePopup();

  const handleChange = e => {
    const { name, value } = e.target;
    setSignup(prev => ({ ...prev, [name]: value }));

    if (name === "name") {
      setErrorState(prev => ({ ...prev, name: validateAlphabetsString(value) }));
    }
    if (name === "email") {
      debouncedCheckEmail(value);
      setErrorState(prev => ({ ...prev, email: validateEmail(value) }));
    }
    if (name === "password") {
      setErrorState(prev => ({ ...prev, password: validatePassword(value) }));
    }
    if (name === "password2") {
      setErrorState(prev => ({ ...prev, password2: value !== signup.password }));
    }
  };

  const isFormValid =
    !errorState.name &&
    !errorState.email &&
    !errorState.password &&
    !errorState.password2 &&
    signup.name.trim() !== "" &&
    signup.email.trim() !== "" &&
    signup.password.trim() !== "" &&
    signup.password2.trim() !== "";

  const checkError = async data => {
    const name = await validateAlphabetsString(data.name);
    const email = await validateEmail(data.email);
    const password = await validatePassword(data.password);
    let password2;
    if (data.password2.length === 0 || data.password !== data.password2) {
      password2 = true;
    } else {
      password2 = false;
    }
    await setErrorState({
      ...errorState,
      name,
      email,
      password,
      password2,
    });
    if (name || email || password || password2) {
      return true;
    }
    return false;
  };

  const debouncedCheckEmail = debounce(async newEmail => {
    if (!validateEmail(newEmail)) {
      try {
        const { data } = await checkEmailQuery({ variables: { email: newEmail } });
        if (data?.checkEmail) {
          setErrorState(prev => ({ ...prev, email: "This email is already registered. Please sign in or use a different email." }));
        } else {
          setErrorState(prev => ({ ...prev, email: false }));
        }
      } catch (err) {
        console.log("GraphQL Error: ", err);
      }
    }
  }, 800);



  const signupUser = async e => {
    setIsLoading(true);
    e.preventDefault();
    const check = await checkError(signup);
    if (check) {
      setIsLoading(false);
      return;
    }
    const encryptedPassword = encryptPassword(signup.password);
    allQuery({
      QUERY: PRESIGNUP,
      variables: {
        data: {
          name: signup.name,
          email: signup.email,
          password: encryptedPassword,
        },
      },
      onCompleteFunc: data => {
        setIsLoading(false);
        if (data?.preSignUp.message) {
          setMessage(data.preSignUp.message);
          setOpen(true);
        }
      },
      onErrorFunc: errdata => {
        setIsLoading(false);
        console.log("err", errdata?.message);
        if (errdata?.message.includes("Welcome Back")) {
          customPopupModel({
            type: popupType.emoji,
            buttonType: popupType.success,
            contentFunc: errdata?.message || "Something went wrong",
          });
        } else {
          customPopupModel({
            type: popupType.failed,
            buttonType: popupType.failed,
            contentFunc: <Typography component="p">{errdata?.message || "Something went wrong"}</Typography>,
          });
        }
      },
    });
  };
  
  const theme = useTheme();
  return (
    <>
      {title ? (
        <Typography fontWeight="700" variant="h3" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}
      <LoginGoogle buttonLabel="signup_with" />
      <Box mt={3}>
        <Divider>
          <Typography component="span" color="textSecondary" variant="h6" fontWeight="400" position="relative" px={2}>
            or sign up with
          </Typography>
        </Divider>
      </Box>
      {open && <MessagePopUp handleCloseAuth message={message} open={open} setOpen={setOpen} />}
      <Box>
        <Stack mb={3}>
          <CustomFormLabel htmlFor="name">Full Name</CustomFormLabel>
          <CustomTextField
            id="name"
            name="name"
            type="text"
            variant="outlined"
            fullWidth
            value={signup.name}
            onChange={e => {
              let value = e.target.value;
              if (value.length === 0) {
                setSignup(prev => ({ ...prev, name: "" }));
                setErrorState(prev => ({ ...prev, name: "empty" }));
                return;
              }
              const containsInvalidCharacters = /[^a-zA-Z\s]/.test(value);

              value = value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/\s+/g, " ")
                .trimStart();

              let charCount = value.replace(/\s/g, "").length;

              if (charCount > 50) {
                setErrorState(prev => ({ ...prev, name: "max" }));
                return;
              } else if (containsInvalidCharacters) {
                setErrorState(prev => ({ ...prev, name: "invalid" }));
              } else if (charCount === 0) {
                setErrorState(prev => ({ ...prev, name: "empty" }));
              } else {
                setErrorState(prev => ({ ...prev, name: false }));
              }

              setSignup(prev => ({ ...prev, name: value }));
            }}
            onBlur={() => {
              setSignup(prev => ({ ...prev, name: prev.name.trim() }));
            }}
            error={errorState.name !== false}
            helperText={
              errorState.name && signup.name.length === 0
                ? "Name field  cannot be empty"
                : errorState.name === "max"
                ? "Name cannot exceed 50 characters."
                : errorState.name === "invalid"
                ? "Name field should contain only alphabets."
                : ""
            }
          />

          <CustomFormLabel htmlFor="email">Email Address</CustomFormLabel>
          <CustomTextField
            id="email"
            name="email"
            type="email"
            variant="outlined"
            fullWidth
            autoComplete="off"
            inputProps={{
              autoComplete: "new-email",
              form: "off",
              autoCapitalize: "none",
              inputMode: "none",
            }}
            error={Boolean((validateEmail(signup.email) && signup.email.length > 0) || (errorState.email && signup.email.length === 0) || errorState.email)}
            value={signup.email}
            onChange={e => handleChange(e)}
            // error={Boolean(errorState.email)}
            helperText={
              validateEmail(signup.email) && signup.email.length > 0
                ? "Please enter a valid email address."
                : errorState.email && signup.email.length === 0
                ? "Email field cannot be empty."
                : errorState.email
            }
            // helperText={errorState.email ? (signup.email.length === 0 ? "Email field cannot be empty." : "Please enter a valid email") : null}
            sx={{
              "& input:is(:autofill)": {
                boxShadow: "0 0 0 100px #e8f0fe inset",
                WebkitTextFillColor: "#000000",
                caretColor: "#000000",
                "& + fieldset": { borderColor: "#DFE5EF" },
              },
            }}
          />
          <CustomFormLabel htmlFor="password">Password</CustomFormLabel>
          <CustomTextField
            id="password"
            name="password"
            type={showPasswordState.password ? "text" : "password"}
            variant="outlined"
            fullWidth
            autoComplete="off"
            inputProps={{
              autoComplete: "new-password",
              form: "off",
              autoCapitalize: "none",
              inputMode: "none",
            }}
            value={signup.password}
            onChange={e => handleChange(e)}
            error={errorState.password}
            helperText={
              errorState.password
                ? "Please enter a strong password. Password should be 8 to 15 characters long and contain at least one small letter, one capital letter, one special character, and one number"
                : "Password should be 8 to 15 characters long and contain at least one small letter, one capital letter, one special character, and one number"
            }
            sx={{
              "& input:is(:autofill)": {
                boxShadow: "0 0 0 100px #e8f0fe inset",
                WebkitTextFillColor: "#000000",
                caretColor: "#000000",
                "& + .MuiInputAdornment-root + fieldset": { borderColor: "#DFE5EF" },
              },
            }}
            InputProps={{
              endAdornment: (
                <PassWordShowIconWrapper position="end">
                  <IconButton onClick={() => togglePasswordVisibility("password")} edge="end">
                    {showPasswordState.password ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </PassWordShowIconWrapper>
              ),
            }}
          />

          <CustomFormLabel htmlFor="password">Confirm Password</CustomFormLabel>
          <CustomTextField
            id="password2"
            name="password2"
            type={showPasswordState.confirmPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            value={signup.password2}
            onChange={e => handleChange(e)}
            error={errorState.password2}
            helperText={errorState.password2 ? (signup.password2.length === 0 ? "Confirm Password field cannot be empty." : "Passwords do not match") : null}
            sx={{
              "& input:is(:autofill)": {
                boxShadow: "0 0 0 100px #e8f0fe inset",
                WebkitTextFillColor: "#000000",
                caretColor: "#000000",
                "& + .MuiInputAdornment-root + fieldset": { borderColor: "#DFE5EF" },
              },
            }}
            InputProps={{
              endAdornment: (
                <PassWordShowIconWrapper position="end">
                  <IconButton onClick={() => togglePasswordVisibility("confirmPassword")} edge="end">
                    {showPasswordState.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </PassWordShowIconWrapper>
              ),
            }}
          />
          <Typography sx={{ mt: "20px", display: "flex", alignItems: "center", gap: "7px", fontWeight: 600 }} component="p">
            <Typography sx={{ fontSize: "20px" }} component="span">
              ✅
            </Typography>{" "}
            Join 1,000+ users who trust our platform
          </Typography>
        </Stack>
        <Button
          sx={{
            backgroundColor: "#5D87FF",
            color: "#fff",
            transition: "0.3s all",
            "&:hover": {
              opacity: "0.9",
              backgroundColor: "#5D87FF",
            },
            "&.Mui-disabled": {
              color: "#ccc",
              backgroundColor: "#e0e0e0",
            },
          }}
          size="large"
          fullWidth
          component={Link}
          to="/"
          onClick={signupUser}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? <CircularProgress sx={{ color: "#ffffff" }} size={26.25} /> : "Get Started"}
        </Button>
      </Box>
      {subtitle}
    </>
  );
}

export default AuthRegister;
